/* eslint-disable no-case-declarations,no-restricted-syntax */
const { validatorFromBetslip, validatorFromBasket } = require('../utils/eligibility');
const { BetModes, AchievementValidator } = require('../types');
const { parseError } = require('../utils');
const {
  checkNotSystem,
  checkMinBetOdds,
  checkMinPlacedAmount,
  checkBetCents,
  checkEligiblePeriod,
  checkStartingBeforeMaxStartDate,
  checkStartingAfterMinStartDate,
  checkAllowedMatchIds,
  checkAllowedTournamentIds,
  checkAllowedCategoryIds,
  checkAllowedSportIds,
  checkAllowedBetTypeIds,
  checkExcludedBetTypeIds,
  checkExcludedSportIds,
  checkExcludedTournamentIds,
  checkExcludedCategoryIds,
  checkExcludedMatchIds,
  checkExcludeOutrightButBoostedOdds,
  checkUsingFreebet,
  checkHasBoostedOdd,
  checkHasOutright,
} = require('../utils/eligibility/validators');

class ValidationError extends Error {}

const checkBasketConditions = (validators, basketConditions) => {
  if (basketConditions && basketConditions.length) {
    const orCondition = [];

    basketConditions.forEach((condition) => {
      const { mode, minCombinedBets, minOddsInCombinedSelection, minMyMatchLines } = condition;

      switch (mode) {
        case BetModes.SIMPLE:
          orCondition.push({
            type: AchievementValidator.IS_SIMPLE_BET,
          });
          break;

        case BetModes.COMBINED:
          const combinedCondition = [];
          combinedCondition.push({ type: AchievementValidator.IS_COMBINED_BET });

          if (minCombinedBets) {
            combinedCondition.push({
              type: AchievementValidator.IS_MIN_COMBINED_BET_VALID,
              parameters: minCombinedBets,
            });
          }

          if (minOddsInCombinedSelection) {
            combinedCondition.push({
              type: AchievementValidator.IS_MIN_ODD_IN_COMBINED_VALID,
              parameters: minOddsInCombinedSelection,
            });
          }

          orCondition.push(combinedCondition);
          break;

        case BetModes.MYMATCH:
          const mymatchCondition = [];
          mymatchCondition.push({ type: AchievementValidator.HAS_MYMATCH });

          if (minMyMatchLines) {
            mymatchCondition.push({
              type: AchievementValidator.IS_MIN_MYMATCH_LINE_VALID,
              parameters: minMyMatchLines,
            });
          }

          orCondition.push(mymatchCondition);
          break;
        default:
      }
    });

    validators.push(orCondition);
  }
};

const getValidators = (eligibility, isStillEligible = false) => {
  const {
    minBetOdds,
    minPlacedAmount,
    betCents,
    minStartDate,
    maxStartDate,
    allowedBetTypes,
    allowedMatchIds,
    allowedSportIds,
    allowedCategoryIds,
    allowedTournamentIds,
    eligiblePeriod,

    excludedBetTypes,
    excludedMatchIds,
    excludedSportIds,
    excludedCategoryIds,
    excludedTournamentIds,
    excludeOutrightButBoostedOdds,

    hasFreebet,
    hasBoostedOdds,
    hasOutright,

    basketConditions,
  } = eligibility;

  const validators = [];

  // We optimized betslips system so they won't ever get in here cause they have been filtered before.
  // If we wan't to handle them later. Do not forget to remove the optimization fix (services/betslip.js)
  checkNotSystem(validators);
  checkMinBetOdds(validators, minBetOdds);
  checkMinPlacedAmount(validators, minPlacedAmount);
  checkBetCents(validators, betCents);

  checkExcludeOutrightButBoostedOdds(validators, excludeOutrightButBoostedOdds);

  checkUsingFreebet(validators, hasFreebet);
  checkHasBoostedOdd(validators, hasBoostedOdds);
  checkHasOutright(validators, hasOutright);

  if (!isStillEligible) {
    checkStartingAfterMinStartDate(validators, minStartDate);
    checkStartingBeforeMaxStartDate(validators, maxStartDate);
    checkEligiblePeriod(validators, eligiblePeriod);
  }

  checkBasketConditions(validators, basketConditions);
  checkAllowedBetTypeIds(validators, allowedBetTypes);

  if (!allowedBetTypes?.length) {
    checkExcludedBetTypeIds(validators, excludedBetTypes);
  }

  if (!allowedMatchIds?.length) {
    checkExcludedMatchIds(validators, excludedMatchIds);
  }

  if (!allowedTournamentIds?.length) {
    checkExcludedTournamentIds(validators, excludedTournamentIds);
  }

  if (!allowedCategoryIds?.length) {
    checkExcludedCategoryIds(validators, excludedCategoryIds);
  }

  if (!allowedSportIds?.length) {
    checkExcludedSportIds(validators, excludedSportIds);
  }

  if (allowedMatchIds?.length) {
    checkAllowedMatchIds(validators, allowedMatchIds);
  } else if (allowedTournamentIds?.length) {
    checkAllowedTournamentIds(validators, allowedTournamentIds);
  } else if (allowedCategoryIds?.length) {
    checkAllowedCategoryIds(validators, allowedCategoryIds);
  } else {
    checkAllowedSportIds(validators, allowedSportIds);
  }

  return validators;
};

const checkValidator = ({ validator, type, params, parameters, logger }) => {
  if (!validator[type]) {
    logger.log('info', `validator: ${type} does not exist`);
    return;
  }

  if (
    ((parameters || (parameters !== null && parameters !== undefined)) &&
      !validator[type](parameters)(...params)) ||
    (!parameters && !validator[type](...params))
  ) {
    throw new ValidationError(`Validator failed: ${type}`);
  }
};

const checkArrayValidator = (validator, params, checks, logger) => {
  let lastError = null;

  for (let i = 0; i < checks.length; i++) {
    const conditions = checks[i];

    try {
      if (Array.isArray(conditions)) {
        for (let j = 0; j < conditions.length; j++) {
          const { parameters, type } = conditions[j];
          checkValidator({ validator, type, params, parameters });
        }
      } else {
        const { type } = conditions;
        checkValidator({ validator, type, params });
      }

      return;
    } catch (e) {
      lastError = e;

      if (e instanceof ValidationError) {
        logger.log('info', 'check array validator failed', {
          error: e.message,
          conditions,
        });
      } else {
        logger.log('info', 'an error occurred during elibility check', {
          error: parseError(e),
          conditions,
        });
      }
    }
  }

  throw lastError;
};

const checkEligibility = ({
  betslip,
  basket,
  item,
  entities,
  eligibility,
  states,
  logger = {
    log: () => {},
  },
}) => {
  const selectedValidators = getValidators(eligibility);
  const params = betslip ? [betslip, states] : [basket, item, entities];
  const validator = betslip ? validatorFromBetslip : validatorFromBasket;

  selectedValidators.forEach((obj) => {
    if (Array.isArray(obj)) {
      checkArrayValidator(validator, params, obj, logger);
    } else {
      const { type, parameters } = obj;

      checkValidator({ validator, type, params, parameters, logger });
    }
  });
};

const checkMissionEligibility = ({
  mission,
  license,
  logger,
  skipLicenceCheck,
  validationDate,
}) => {
  const { startDate, endDate, licenses, missionId } = mission;

  if (!validationDate || validationDate < startDate || validationDate > endDate) {
    logger.log('info', 'mission currently not running', {
      startDate,
      endDate,
      missionId,
      validationDate,
    });
    return false;
  }

  if (!skipLicenceCheck) {
    if (!licenses?.includes(license)) {
      logger.log('info', 'mission not eligible for this license', {
        eligibleLicenses: licenses,
        license,
        missionId,
      });
      return false;
    }
  }

  return true;
};

const isBetEligible = ({
  betslip,
  states,
  basket,
  item,
  entities,
  eligibility,
  logger = { log: () => {} },
}) => {
  try {
    checkEligibility({
      betslip,
      eligibility,
      basket,
      item,
      entities,
      states,
      logger,
    });
    return true;
  } catch (e) {
    if (e instanceof ValidationError) {
      logger.log('info', 'bet not eligible', {
        error: e.message,
        eligibility,
        betslip,
        basket,
        item,
      });
    } else {
      logger.log('info', 'an error occurred during eligibility check', {
        error: parseError(e),
        betslip,
      });
    }
  }

  return false;
};

const findEligibleAchievementFromBasket = ({
  missions,
  achievements,
  basket,
  item,
  entities,
  runtime,
  logger = {
    log: () => {},
  },
}) => {
  const { licence } = runtime;

  const missionIds = Object.keys(missions ?? {});

  for (let i = 0; i < missionIds.length; i++) {
    const missionId = missionIds[i];
    const mission = missions[missionId];

    if (mission) {
      const missionChecks = checkMissionEligibility({
        mission,
        licence,
        logger,
        skipLicenceCheck: true,
        validationDate: Date.now(),
      });

      if (missionChecks) {
        const { achievementIds } = mission;

        for (const achievementId of achievementIds) {
          const { eligibility } = achievements?.[achievementId] ?? {};

          if (isBetEligible({ basket, item, entities, eligibility, logger })) {
            return { achievementId, missionId };
          }
        }
      } else {
        logger.log('info', "betslip didn't pass mission checks", { mission });
      }
    }
  }

  return null;
};

const findEligibleAchievementFromBetslip = ({
  missions,
  betslip,
  userAchievementIds,
  states,
  logger = {
    log: () => {},
  },
}) => {
  const {
    user: { license },
    freebet,
  } = betslip;

  if (freebet) {
    logger.log('info', 'betslip is placed with freebet', { betslip });
    return null;
  }

  for (let i = 0; i < missions.length; i++) {
    const mission = missions[i];
    const { achievements } = mission;
    const missionChecks = checkMissionEligibility({
      mission,
      license,
      logger,
      validationDate: betslip.validationDate ?? Date.now(),
    });

    if (missionChecks) {
      const achievementsArray = Object.values(achievements);

      for (let j = 0; j < achievementsArray.length; j++) {
        const { eligibility, achievementId, missionId } = achievementsArray[j];

        if (userAchievementIds.includes(achievementId)) {
          logger.log('info', 'user already have this achievement', {
            betslip,
            userAchievementIds,
            missionId,
            achievementId,
          });
        } else if (isBetEligible({ betslip, eligibility, states, logger })) {
          return { achievementId, missionId };
        }
      }
    } else {
      logger.log('info', "betslip didn't pass mission checks", { mission, betslip });
    }
  }

  return null;
};

const isBetslipStillEligible = ({ betslip, achievement, states, logger = { log: () => {} } }) => {
  if (!achievement) {
    logger.log('notice', 'achievement does not exist', { betslip });
    return false;
  }

  const { eligibility } = achievement;
  const validators = getValidators(eligibility, true);
  const validator = validatorFromBetslip;
  const params = [betslip, states];

  try {
    validators.forEach((obj) => {
      if (Array.isArray(obj)) {
        checkArrayValidator(validator, params, obj, logger);
      } else {
        const { type, parameters } = obj;
        checkValidator({ validator, type, params, parameters });
      }
    });
  } catch (e) {
    if (e instanceof ValidationError) {
      logger.log('info', 'achievement no longer eligible', {
        error: e.message,
        betslip,
        achievement,
      });
    } else {
      logger.log('info', 'an error occurred during stillEligible check', {
        error: parseError(e),
        betslip,
        achievement,
      });
    }
    return false;
  }

  return true;
};

module.exports = {
  checkEligibility,
  findEligibleAchievementFromBetslip,
  findEligibleAchievementFromBasket,
  isBetslipStillEligible,
  isBetEligible,
};
